import { CountryIntelligenceOysterScore } from "@oysterhr/schema"
import { Badge, Icon, Text, Tooltip, validateDisplayNameChild, BadgeVariation } from "@oysterhr/ui"
import classNames from "classnames"
import { BenefitsDetail } from "../../../hooks/useBenefitsOptionsForCountry"
import { Children, ReactNode, createContext, isValidElement, useContext } from "react"
import { useTranslation } from "react-i18next"
import { multipleDurationsWithUnit } from "../../../utils/benefits"
import { capitalize } from "lodash-es"

const BenefitsItemContext = createContext<{
  hasCheckmark?: boolean
}>({ hasCheckmark: false })

const useBenefitsItemContext = () => {
  const context = useContext(BenefitsItemContext)
  if (!context) {
    throw new Error("useBenefitsItemContext must be used within a BenefitsItem")
  }
  return context
}

export const BenefitsItem = ({
  children,
  hasCheckmark,
}: {
  children: ReactNode
  hasCheckmark?: boolean
}) => {
  const titleNode = Children.toArray(children).filter(child => {
    return isValidElement(child) && validateDisplayNameChild(child, "BenefitsItem.Title")
  })

  const descriptionNode = Children.toArray(children).filter(child => {
    return isValidElement(child) && validateDisplayNameChild(child, "BenefitsItem.Description")
  })

  const detailsNode = Children.toArray(children).filter(child => {
    return isValidElement(child) && validateDisplayNameChild(child, "BenefitsItem.Details")
  })

  return (
    <BenefitsItemContext.Provider value={{ hasCheckmark }}>
      <div className="flex items-center gap-2">
        {hasCheckmark && (
          <Icon name="CheckCircle" className="mr-1 shrink-0 text-success-base" size="sm" />
        )}
        <div>
          {titleNode}
          {descriptionNode}
          {detailsNode}
        </div>
      </div>
    </BenefitsItemContext.Provider>
  )
}

const BenefitsItemTitle = ({
  badgeText,
  badgeVariation,
  tooltip,
  children,
}: {
  badgeText?: string | null
  badgeVariation?: BadgeVariation | null
  tooltip?: string | null
  children: string
}) => {
  const { hasCheckmark } = useBenefitsItemContext()
  return (
    <div className="flex items-center gap-2">
      <div>
        <Text
          className={classNames({
            "min-h-6": !hasCheckmark,
          })}
        >
          {children}
        </Text>
      </div>
      {badgeText ? (
        tooltip ? (
          <Tooltip value={tooltip}>
            <Badge variation={badgeVariation ?? undefined}>{badgeText}</Badge>
          </Tooltip>
        ) : (
          <Badge variation={badgeVariation ?? undefined}>{badgeText}</Badge>
        )
      ) : null}
    </div>
  )
}
BenefitsItemTitle.displayName = "BenefitsItem.Title"

const BenefitsItemDescription = ({ children }: { children?: string | null }) => {
  if (children == null) return null
  return (
    <Text size="small" className="text-gray-400">
      {children}
    </Text>
  )
}
BenefitsItemDescription.displayName = "BenefitsItem.Description"

const BenefitsDetailsBadge = ({ oysterScore }: { oysterScore: CountryIntelligenceOysterScore }) => {
  const { t } = useTranslation()
  if (!oysterScore) return null
  if (oysterScore === CountryIntelligenceOysterScore.Statutory) return null

  const variationMap: Record<CountryIntelligenceOysterScore, BadgeVariation> = {
    [CountryIntelligenceOysterScore.Customary]: "gray" as const,
    [CountryIntelligenceOysterScore.Recommended]: "gray" as const,
    [CountryIntelligenceOysterScore.TopOfMarket]: "info" as const,
    [CountryIntelligenceOysterScore.Statutory]: "gray" as const,
  }

  type possibleTooltips =
    | typeof CountryIntelligenceOysterScore.Customary
    | typeof CountryIntelligenceOysterScore.TopOfMarket

  const tooltipMap: Record<possibleTooltips, string> = {
    [CountryIntelligenceOysterScore.Customary]: t("tooltipForOysterScore.CUSTOMARY", {
      ns: "salary_insights_tool/benefits",
    }),
    [CountryIntelligenceOysterScore.TopOfMarket]: t("tooltipForOysterScore.TOP_OF_MARKET", {
      ns: "salary_insights_tool/benefits",
    }),
  }
  const badge = (
    <Badge variation={variationMap[oysterScore]}>
      {capitalize(t(`enums.oysterScore.${oysterScore}`, { ns: "salary_insights_tool/benefits" }))}
    </Badge>
  )

  if (oysterScore in tooltipMap)
    return <Tooltip value={tooltipMap[oysterScore as possibleTooltips]}>{badge}</Tooltip>
  return badge
}

const BenefitsItemDetails = ({ details }: { details?: BenefitsDetail[] | null }) => {
  const { t } = useTranslation("salary_insights_tool/benefits")
  if (details == null || details?.length === 0) return null

  const TimeDuration = ({ detail }: { detail: BenefitsDetail }) => {
    if (detail?.durationEnd) {
      return (
        <>
          {multipleDurationsWithUnit(t, detail?.duration, detail?.durationEnd)}{" "}
          {t("timeOffSubdivision")}
        </>
      )
    }
    return <>{multipleDurationsWithUnit(t, detail?.duration)}</>
  }

  return (
    <div className="flex flex-col gap-1">
      {details.map(detail => (
        <div key={detail?.duration?.value} className="flex items-center gap-2">
          <Text size="small" className="text-gray-400">
            <TimeDuration detail={detail} />
          </Text>
          <BenefitsDetailsBadge oysterScore={detail?.oysterScore} />
        </div>
      ))}
    </div>
  )
}

BenefitsItemDetails.displayName = "BenefitsItem.Details"

BenefitsItem.Title = BenefitsItemTitle
BenefitsItem.Description = BenefitsItemDescription
BenefitsItem.Details = BenefitsItemDetails
